import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Icon, Grid, Form, Button, Label } from 'semantic-ui-react';
import { TextWidget } from '@plone/volto/components';

import { map } from 'lodash';

import './index.css';
import { messages } from './messages';
const defaultLink = () => ({
  title: '',
  url: '',
});

const defaultItem = (title) => ({
  title: title || '',
  links: [defaultLink()],
});

const defaultConfiguration = () => [defaultItem()];

const PortalFooterChannelsWidget = ({
  value,
  id,
  onChange,
  required,
  title,
  description,
  error,
}) => {
  const intl = useIntl();
  const [configuration, setConfiguration] = useState(
    value ? JSON.parse(value) : defaultConfiguration,
  );

  const handleChangeConfiguration = (value) => {
    setConfiguration(value);
    onChange(id, JSON.stringify(value));
  };

  const addItem = (e) => {
    e.preventDefault();

    let newConfiguration = [
      ...configuration,
      {
        ...defaultItem(),
      },
    ];

    handleChangeConfiguration(newConfiguration);
  };

  const moveItem = (e, index, direction) => {
    e.preventDefault();
    const up = direction === 'up';
    let newConfiguration = [...configuration];

    let item = newConfiguration[index];
    newConfiguration.splice(index, 1);
    newConfiguration.splice(index + (up ? -1 : 1), 0, item);

    handleChangeConfiguration(newConfiguration);
  };

  const onChangeItem = (index, item) => {
    let newConfiguration = [...configuration];
    newConfiguration[index] = item;

    handleChangeConfiguration(newConfiguration);
  };

  const deleteItem = (e, index) => {
    e.preventDefault();
    let newConfiguration = [...configuration];
    newConfiguration.splice(index, 1);

    handleChangeConfiguration(newConfiguration);
  };

  const addLink = (e, index) => {
    e.preventDefault();
    let newConfiguration = [...configuration];
    const item = { ...newConfiguration[index] };
    item.links = [...(item.links || []), defaultLink()];
    newConfiguration[index] = item;

    handleChangeConfiguration(newConfiguration);
  };

  const deleteLink = (e, index, linkIndex) => {
    e.preventDefault();
    let newConfiguration = [...configuration];
    const item = { ...newConfiguration[index] };
    item.links = (item.links || []).filter((_, i) => i !== linkIndex);
    newConfiguration[index] = item;

    handleChangeConfiguration(newConfiguration);
  };

  const onChangeLink = (index, linkIndex, field, value) => {
    let newConfiguration = [...configuration];
    const item = { ...newConfiguration[index] };
    const links = [...(item.links || [])];
    links[linkIndex] = { ...links[linkIndex], [field]: value };
    item.links = links;
    newConfiguration[index] = item;

    handleChangeConfiguration(newConfiguration);
  };

  return (
    <div className="portal-footer-channels-widget">
      <Form.Field inline id={id} error={error.length > 0}>
        <Grid>
          {
            <Grid.Row>
              <Grid.Column width="12">
                <div className="wrapper">
                  <label htmlFor="footer-configuration">{title}</label>
                </div>
              </Grid.Column>
            </Grid.Row>
          }
          {description && (
            <Grid.Row>
              <Grid.Column stretched width="12">
                <p className="help">{description}</p>
              </Grid.Column>
            </Grid.Row>
          )}
          <Grid.Row>
            <Grid.Column width="12" className="portal-footer-channels-widget">
              <Grid stackable celled="internally">
                {configuration.map((item, idx) => (
                  <Grid.Row key={idx}>
                    <Grid.Column width={1}>
                      <div className="preview-social">
                        <Button.Group vertical className="move-buttons">
                          <Button
                            disabled={idx === 0}
                            size="tiny"
                            icon={<Icon name="arrow left" />}
                            title={intl.formatMessage(messages.moveItemUp)}
                            onClick={(e) => moveItem(e, idx, 'up')}
                          />
                          <Button
                            disabled={idx === configuration.length - 1}
                            size="tiny"
                            icon={<Icon name="arrow right" />}
                            title={intl.formatMessage(messages.moveItemDown)}
                            onClick={(e) => moveItem(e, idx, 'down')}
                          />
                        </Button.Group>
                      </div>
                    </Grid.Column>
                    <Grid.Column width={11}>
                      {map(error, (message) => (
                        <Label key={message} basic color="red" pointing>
                          {message}
                        </Label>
                      ))}
                      <TextWidget
                        id={`${idx}-title`}
                        title={intl.formatMessage(messages.title)}
                        description=""
                        required={true}
                        value={item.title}
                        onChange={(id, value) => {
                          onChangeItem(idx, {
                            ...configuration[idx],
                            title: value?.length ? value : '',
                          });
                        }}
                      />
                      <div className="portal-footer-channel-links">
                        <span className="portal-footer-channel-links__label">
                          {intl.formatMessage(messages.links)}
                        </span>
                        {(item.links || []).map((link, linkIdx) => (
                          <div
                            className="portal-footer-channel-link"
                            key={linkIdx}
                          >
                            <div className="portal-footer-channel-link__header">
                              <span className="portal-footer-channel-link__badge">
                                {intl.formatMessage(messages.linkNumber)}{' '}
                                {linkIdx + 1}
                              </span>
                              <Button
                                type="button"
                                icon="trash"
                                basic
                                size="mini"
                                className="portal-footer-channel-link__delete"
                                title={intl.formatMessage(messages.deleteLink)}
                                aria-label={intl.formatMessage(
                                  messages.deleteLink,
                                )}
                                onClick={(e) => deleteLink(e, idx, linkIdx)}
                              />
                            </div>
                            <div className="portal-footer-channel-link__fields">
                              <TextWidget
                                id={`${idx}-link-${linkIdx}-title`}
                                title={intl.formatMessage(messages.linkName)}
                                description=""
                                required={true}
                                value={link.title}
                                onChange={(id, value) =>
                                  onChangeLink(
                                    idx,
                                    linkIdx,
                                    'title',
                                    value?.length ? value : '',
                                  )
                                }
                              />
                              <TextWidget
                                id={`${idx}-link-${linkIdx}-url`}
                                title={intl.formatMessage(messages.linkUrl)}
                                description=""
                                required={true}
                                value={link.url}
                                onChange={(id, value) =>
                                  onChangeLink(
                                    idx,
                                    linkIdx,
                                    'url',
                                    value?.length ? value : '',
                                  )
                                }
                              />
                            </div>
                          </div>
                        ))}
                        <Button
                          type="button"
                          size="small"
                          icon={<Icon name="plus" />}
                          content={intl.formatMessage(messages.addLink)}
                          className="portal-footer-channel-links__add"
                          onClick={(e) => addLink(e, idx)}
                        />
                      </div>
                      <div className="portal-footer-channels-settings">
                        <Button
                          icon="trash"
                          onClick={(e) => deleteItem(e, idx)}
                          content={intl.formatMessage(messages.deleteItem)}
                          size="tiny"
                        />
                      </div>
                    </Grid.Column>
                  </Grid.Row>
                ))}

                {/* add row */}
                <Grid.Row>
                  <Grid.Column width={1}>
                    <Button
                      size="tiny"
                      icon={<Icon name="plus" />}
                      onClick={(e) => addItem(e)}
                    />
                  </Grid.Column>
                  <Grid.Column></Grid.Column>
                </Grid.Row>
              </Grid>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form.Field>
    </div>
  );
};
PortalFooterChannelsWidget.defaultProps = {
  error: [],
};
export default PortalFooterChannelsWidget;
